import React, { useState, useRef, useEffect } from "react";
import ActionBar from "../../common/ActionBar";
import { Container, HyperLink, StyledTitle, TableWrapper } from "./Styles";
import _ from "lodash";
import CustomTable from "../../common/CustomTable/CustomTable";
import labels from "../../../helpers/labels.json";
import CaseService from "../../../services/CaseService";
import { useSnackbar } from "notistack";
import queryString from "query-string";
import {
  getErrorMessage,
  parseTimeStamp,
  renderFeeKind,
} from "../../../helpers/functions";
import { pending_action_msg } from "../../../helpers/constants";
import useLoader from "../../../hooks/useLoader";
import moment from "moment";
import { navigate } from "@reach/router";
import useNotification from "../../../hooks/useNotification";
import ConfirmArbitrator from "./component/ConfirmArbitrator";
import useSettings from "../../../hooks/useSettings";
import ReOpenModal from "./component/ReOpenCase";
import { ActionButton, PrimaryCTAButton } from "../../common/Buttons";
import EditModal from "./component/EditModal";
import theme from "../../../assets/theme";
import { ButtonContainer, DangerBtn, Footer } from "../FinalApprover/Styles";
import AlertDialog from "../FinalApprover/component/AlertWrapper";

async function getPendingActions(query = "", kind) {
  try {
    const response = await CaseService.getPendingActions(query);
    let stats = {
      allCount:
        kind !== "caseManagerApproveCase" &&
        kind !== "caseManagerReviewDocumentUpload" &&
        kind !== "respondentRequestNegotiationSuccessFee" &&
        kind !== "caseManagerSummaryUpload" &&
        kind !== "caseManagerAgreementUpload"
          ? response.activities.total
          : 0,
      caseManagerApproveCaseCount:
        kind === "caseManagerApproveCase" ? response.activities.total : 0,
      caseManagerReviewDocumentUploadCount:
        kind === "caseManagerReviewDocumentUpload"
          ? response.activities.total
          : 0,
      respondentRequestNegotiationSuccessFeeCount:
        kind === "respondentRequestNegotiationSuccessFee"
          ? response.activities.total
          : 0,
      caseManagerSummaryUploadCount:
        kind === "caseManagerSummaryUpload" ? response.activities.total : 0,
      caseManagerAgreementUploadCount:
        kind === "caseManagerAgreementUpload" ? response.activities.total : 0,
    };
    return { ...response, stats };
  } catch (error) {
    throw error;
  }
}

const initFilters = [
  { label: "All", key: "allCount", value: 0 },
  {
    label: "Approve Case",
    key: "caseManagerApproveCaseCount",
    value: 0,
  },
  { label: "View Case", key: "caseManagerReviewDocumentUploadCount", value: 0 },
  {
    label: "Send Reminder",
    key: "respondentRequestNegotiationSuccessFeeCount",
    value: 0,
  },
  { label: "Upload Summary", key: "caseManagerSummaryUploadCount", value: 0 },
  {
    label: "Upload Agreement",
    key: "caseManagerAgreementUploadCount",
    value: 0,
  },
];

const filterByStatus = (selectedFilter) => {
  let key = "";
  switch (selectedFilter) {
    case "Approve Case":
      key = "caseManagerApproveCase";
      break;
    case "View Case":
      key = "caseManagerReviewDocumentUpload";
      break;
    case "Send Reminder":
      key = "respondentRequestNegotiationSuccessFee";
      break;
    case "Upload Summary":
      key = "caseManagerSummaryUpload";
      break;
    case "Upload Agreement":
      key = "caseManagerAgreementUpload";
      break;
    default:
      break;
  }
  return key;
};

const Index = () => {
  const breadcrumbs = [
    _.startCase(labels.my_tasks),
    _.startCase(labels.pending_actions),
  ];
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const placeholderText = "Search";
  const [state, setState] = useState({});
  const MTRef = useRef();
  const { triggerNotify, setTriggerNotify } = useNotification(); // hooks to refresh the page
  const [modal, setModal] = useState({ state: false });
  const { setting } = useSettings();
  const [reOpen, setReOpen] = useState({ state: false });
  const [editModal, setEditModal] = useState({ state: false });
  const [selectedCases, setSelectedCases] = useState([]);
  const [dialogData, setDialogData] = useState({});
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState(initFilters);
  const [selectedFilter, setSelectedFilter] = useState(filters[0].label);
  const [find, setFind] = useState(true);

  useEffect(() => {
    if (selectedFilter && MTRef?.current?.onQueryChange) {
      MTRef.current.onQueryChange({
        ...MTRef.current.state.query,
        page: 0,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  function setStats(stats) {
    const mapped = initFilters.map((filter) => {
      let returnData = {
        label: filter.label,
      };
      if (stats[filter.key] >= 0) {
        returnData.value = stats[filter.key];
      }
      return returnData;
    });
    setFilters(mapped);
  }

  const sendRemaider = async ({ kind, entity }) => {
    try {
      setLoader({ state: true, message: "Sending Reminder..." });
      const response = await CaseService.sendRemainder(kind, entity.id);
      if (response?.message) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
      setTriggerNotify(true);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const caseManagerApproveCase = async (id) => {
    try {
      setLoader({ state: true, message: "Approve Case..." });
      const payload = {
        status: "awaitingRespondentAcceptance",
        counterClaimDeadline: setting?.counterClaimDeadline,
      };
      const response = await CaseService.updateCaseManagerCases(id, payload);
      if (response?.message) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
      setTriggerNotify(true);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const pendingAction = (rowData, kind) => {
    if (
      kind === "caseManagerSummaryUpload" ||
      kind === "caseManagerAgreementUpload"
    ) {
      navigate("/dashboard/case-manager/uploads");
    } else if (
      kind === "caseManagerReviewCounterClaim" ||
      kind === "mediatorReviewCounterClaim" ||
      kind === "caseManagerReviewDocumentUpload"
    ) {
      navigate(`/dashboard/case-manager/mediation/${rowData?.entity?.id}`);
    } else if (kind === "caseManagerConfirmArbitrator") {
      setModal({ state: true, rowData: rowData, id: rowData?.entity?.id });
    } else if (kind === "caseManagerApproveCase") {
      caseManagerApproveCase(rowData?.entity?.id);
    } else if (kind === "caseManagerReOpenCase") {
      setReOpen({ state: true, rowData: rowData, id: rowData?.entity?.id });
    } else {
      sendRemaider(rowData);
    }
  };

  const columns = [
    {
      field: "entityId",
      title: labels.case_id,
      sorting: true,
      cellStyle: {
        width: "10%",
        paddingLeft: 20,
      },
      headerStyle: {
        width: "10%",
        paddingLeft: 20,
      },
      render: (rowData) => (
        <HyperLink
          onClick={() =>
            navigate(
              rowData?.entity?.caseManagerId
                ? `/dashboard/case-manager/${rowData?.entity?.resolutionKind}/${rowData?.entity?.id}`
                : `/dashboard/case-manager/${rowData?.entity?.resolutionKind}/view/${rowData?.entity?.id}`,
            )
          }
        >
          {rowData?.entity?.id}
        </HyperLink>
      ),
    },
    {
      field: "case_title",
      title: labels.case_title,
      sorting: false,
      cellStyle: {
        width: "15%",
      },
      headerStyle: {
        width: "20%",
      },
      render: (rowData) => (
        <StyledTitle
          style={{ width: "180px", wordBreak: "break-word" }}
          editFlag={rowData?.entity?.editFlag}
        >
          {rowData?.entity?.title}
        </StyledTitle>
      ),
    },
    {
      field: "claim_party",
      title: labels.claim_party,
      sorting: true,
      cellStyle: {
        width: "15%",
      },
      headerStyle: {
        width: "15%",
      },
      render: (rowData) => <>{rowData?.entity?.claimantParty?.name}</>,
    },
    {
      field: "counter_party",
      title: labels.counter_party,
      sorting: true,
      cellStyle: {
        width: "15%",
      },
      headerStyle: {
        width: "15%",
      },
      render: (rowData) => (
        <>
          {rowData?.entity?.respondentParty?.name ? (
            rowData.entity?.respondentParty.name
          ) : rowData?.entity?.respondentName ? (
            rowData.entity?.respondentName
          ) : rowData?.entity?.respondentParties?.length ? (
            rowData?.entity?.respondentParties[0]?.name
          ) : (
            <div style={{ marginLeft: 35 }}>-</div>
          )}
        </>
      ),
    },
    {
      field: "pending_action",
      title: _.startCase(labels.pending_actions).substr(
        0,
        labels.pending_actions.length - 1,
      ),
      sorting: false,
      cellStyle: {
        width: "15%",
      },
      headerStyle: {
        width: "15%",
      },
      render: (rowData) => (
        <>
          {rowData?.entity?.editFlag === 1
            ? "Sent to Claimant for Edit"
            : renderFeeKind(rowData?.message, rowData?.meta)}
        </>
      ),
    },
    {
      field: "created_at",
      title: labels.target_date,
      sorting: true,
      cellStyle: {
        width: "15%",
      },
      headerStyle: {
        width: "15%",
      },
      render: (rowData) => (
        <>
          {pending_action_msg[rowData?.kind]?.isPayable
            ? moment(
                rowData.submittedOn
                  ? rowData.submittedOn
                  : parseTimeStamp(rowData.created_at),
              )
                .add(process.env.REACT_APP_BASE_TARGET_DATE, "days")
                .format("DD/MM/YYYY")
            : ""}
        </>
      ),
    },
    {
      field: "actions",
      title: labels.actions,
      sorting: false,
      cellStyle: {
        width: "20%",
      },
      headerStyle: {
        width: "20%",
      },
      render: (rowData) =>
        rowData?.kind === "caseManagerApproveCase" ? (
          <div style={{ display: "flex", width: "180px" }}>
            <ActionButton
              style={{
                margin: 0,
                height: 30,
                width: 90,
                fontSize: 10,
                marginRight: 10,
              }}
              onClick={() => caseManagerApproveCase(rowData?.entity?.id)}
            >
              Approve Case
            </ActionButton>
            <ActionButton
              style={{ margin: 0, height: 30, width: 90, fontSize: 10 }}
              onClick={() =>
                setEditModal({ state: true, caseId: rowData?.entity?.id })
              }
            >
              Edit
            </ActionButton>
          </div>
        ) : (
          <ActionButton
            style={{ margin: 0, height: 30, width: "120px", fontSize: 10 }}
            onClick={() => pendingAction(rowData, rowData?.kind)}
          >
            {pending_action_msg[rowData?.kind]?.linkText}
          </ActionButton>
        ),
    },
  ];

  const data = (query) =>
    new Promise((resolve) => {
      let params = {
        page: query.page + 1,
        kind: filterByStatus(selectedFilter),
        perPage: 10,
        search: query.search,
      };
      if (query.orderBy?.field) {
        params.sort = query.orderBy.field;
        params.sortDirection = query.orderDirection;
      }
      let stringParams = "";
      if (!_.isEmpty(params)) {
        stringParams = `?${queryString.stringify(params)}`;
      }
      if (params.kind === "caseManagerApproveCase" || params.kind === "") {
        setFind(true);
      } else {
        setFind(false);
      }
      getPendingActions(stringParams, params.kind)
        .then(async (result) => {
          if (result) {
            resolve({
              data: result?.activities?.data,
              page: result?.activities?.page - 1,
              total: result?.activities?.total,
            });
            setState(result?.activities);
            setStats(result.stats);
          }
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), {
            variant: "error",
          });
          setState({ data: [], lastPage: 1, page: 1, perPage: 10, total: 0 });
          resolve({
            data: [],
            page: 0,
            total: 0,
          });
        });
    });

  useEffect(() => {
    if (triggerNotify) {
      MTRef.current.onSearchChange("");
      setTriggerNotify(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerNotify]);

  const handleCaseSelection = (cases) => {
    const invalid = cases.some(
      (item) => item?.kind !== "caseManagerApproveCase",
    );
    if (invalid) {
      enqueueSnackbar("Only Case Manager Approve Case rows can be selected", {
        variant: "error",
      });
      if (MTRef.current) {
        MTRef.current.onQueryChange("");
      }
      return;
    }
    const caseIds = cases.map((item) => item?.entity?.id);
    setSelectedCases(caseIds);
  };

  const updateBulkCase = async (selectedCases) => {
    try {
      setLoader({ state: true, message: "Approving Cases..." });

      const payload = {
        status: "awaitingRespondentAcceptance",
        counterClaimDeadline: setting?.counterClaimDeadline,
      };

      await Promise.all(
        selectedCases.map((id) =>
          CaseService.updateCaseManagerCases(id, payload),
        ),
      );

      enqueueSnackbar(`Successfully approved ${selectedCases.length} case(s)`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setOpen(false);
      setDialogData({});
      MTRef.current.onQueryChange("");
    }
  };

  const handleClear = () => {
    if (MTRef.current) {
      MTRef.current.onQueryChange("");
      setSelectedCases([]);
      setSelectedCases([]);
    }
  };

  return (
    <>
      <ActionBar {...{ breadcrumbs }} />
      <Container className="PendingActions">
        <TableWrapper>
          <CustomTable
            hidePagination={state.lastPage <= 1}
            pageSize={state?.data?.length ? state?.data?.length : 10}
            pluralTitle="Pending Actions"
            singularTitle=""
            selection={find}
            onSelectionChange={handleCaseSelection}
            {...{
              columns,
              data,
              placeholderText,
              MTRef,
              filters,
              selectedFilter,
              setSelectedFilter,
            }}
            // noToolbar
            // hideFilterBar
            state={state}
          />
        </TableWrapper>

        {selectedCases?.length > 0 ? (
          <Footer>
            <ButtonContainer>
              <div className="button-row">
                <DangerBtn
                  style={{
                    width: "216px",
                    marginLeft: "26px",
                    marginTop: 20,
                  }}
                  onClick={handleClear}
                >
                  Clear
                </DangerBtn>
                <PrimaryCTAButton
                  style={{
                    width: "216px",
                    marginLeft: "26px",
                    marginTop: 20,
                  }}
                  onClick={() => {
                    setOpen(true);
                    setDialogData({
                      primaryBtnText: "Cancel",
                      secondaryBtnText: "Submit",
                      clickSecondarybtn: (reasonText) =>
                        updateBulkCase(selectedCases),
                      clickPrimaryBtn: () => setOpen(false),
                      onDialogClose: () => setOpen(false),
                      desc: `Are you sure you want to approve the following ${selectedCases.length} case(s)`,
                      heading: "Approve Cases",
                      status: true,
                      notTextArea: false,
                      descriptionTextStyle: {
                        textAlign: "left",
                        fontFamily: theme.fonts.primaryFontSemiBold,
                      },
                    });
                  }}
                >
                  Approve Case
                </PrimaryCTAButton>
              </div>
            </ButtonContainer>
          </Footer>
        ) : null}
        {modal?.state && (
          <ConfirmArbitrator {...{ modal, setModal, setTriggerNotify }} />
        )}
        {reOpen?.state && (
          <ReOpenModal {...{ reOpen, setReOpen, setTriggerNotify }} />
        )}
        {editModal?.state && (
          <EditModal {...{ editModal, setEditModal, setTriggerNotify }} />
        )}
        <AlertDialog
          isOpen={open}
          {...{ ...dialogData }}
          descriptionTextStyle={{
            textAlign: "left",
            fontFamily: theme.fonts.primaryFontSemiBold,
          }}
        />
      </Container>
    </>
  );
};

export default Index;
